import { accessIconMap } from 'app/modules/products/components/accessIconMap';
import { UserStatus } from 'app/types/UserStatus';
import cx from 'classnames';
import { UserStatusDot } from "../../core/components/StatusDot/ContractStatusDot";
import { constantToDisplayName } from "../../core/constantToDisplayName";
import { accountOwnershipToDisplayName } from "./accountOwnershipToDisplayName";
import { AccessType, RequireAuth, UserRole } from 'legoland-sdk/dist/experimental';
import { ProductLogo } from "../../products/components/ProductLogo";
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Button, Text, TooltipOnHover, tombac } from 'tombac';
import { LoginAsIcon, TimeIcon } from 'tombac-icons';
import "./UsersList.css";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const WrappedDateTime = styled(Text).withConfig({
  displayName: "createUsersListColumns__WrappedDateTime"
})(["", " white-space:break-spaces;"], tombac.text({
  fontSize: 12,
  color: tombac.color('neutral', 700)
}));
const UserLink = styled(Link).withConfig({
  displayName: "createUsersListColumns__UserLink"
})(["", " color:", ";"], tombac.text({
  fontWeight: 500
}), _ref => {
  let {
    $unnamed
  } = _ref;
  return $unnamed ? tombac.color('neutral', 600) : undefined;
});
function capitalizeFirstLetter(string) {
  return string[0].toUpperCase() + string.slice(1).toLowerCase();
}
export function createUsersListColumns(variant, role, formatDateBySettings, loginAsUser) {
  return [{
    Header: 'Email',
    accessor: 'email',
    width: 'auto',
    Cell: _ref2 => {
      let {
        value,
        row
      } = _ref2;
      return /*#__PURE__*/_jsxs(UserLink, {
        $unnamed: !value,
        to: `/users/edit/${encodeURIComponent(value)}`,
        children: [/*#__PURE__*/_jsx(UserStatusDot, {
          $display: "inline-block",
          $mr: "1sp",
          status: row.original.status
        }), value]
      });
    }
  }, ...(role !== UserRole.Distributor ? [{
    Header: 'Role',
    id: 'role',
    accessor: 'role',
    width: 90,
    Cell: _ref3 => {
      let {
        value
      } = _ref3;
      return /*#__PURE__*/_jsx(_Fragment, {
        children: capitalizeFirstLetter(value)
      });
    }
  }] : []), {
    Header: 'Organization',
    id: 'organization.name',
    accessor: 'organization',
    width: 120,
    Cell: _ref4 => {
      let {
        value
      } = _ref4;
      return /*#__PURE__*/_jsx(_Fragment, {
        children: value.name
      });
    }
  }, ...(role !== UserRole.Distributor ? [{
    Header: 'Account ownership',
    id: 'accountOwnership',
    accessor: 'accountOwnership',
    width: 160,
    Cell: _ref5 => {
      let {
        value
      } = _ref5;
      return /*#__PURE__*/_jsx(_Fragment, {
        children: accountOwnershipToDisplayName(value)
      });
    },
    disableSortBy: true
  }] : []), {
    Header: 'Created at',
    id: 'createdAt',
    accessor: 'createdAt',
    width: 100,
    Cell: _ref6 => {
      let {
        value
      } = _ref6;
      return /*#__PURE__*/_jsx(WrappedDateTime, {
        children: formatDateBySettings(value)
      });
    }
  }, {
    Header: 'Last login',
    id: 'lastActivity',
    accessor: 'lastActivity',
    width: 100,
    Cell: _ref7 => {
      let {
        value
      } = _ref7;
      return /*#__PURE__*/_jsx(_Fragment, {
        children: value ? /*#__PURE__*/_jsx(WrappedDateTime, {
          children: formatDateBySettings(value)
        }) : '-'
      });
    }
  }, {
    Header: 'Products',
    accessor: 'products',
    Cell: _ref8 => {
      let {
        value
      } = _ref8;
      return /*#__PURE__*/_jsx("div", {
        className: "UserItem__products",
        children: /*#__PURE__*/_jsx("div", {
          className: "UserItem__products-container",
          children: value.filter(p => p.access.status !== 'DENIED').map(p => /*#__PURE__*/_jsx(ProductStatus, {
            product: p
          }, p.id))
        })
      });
    },
    width: 280,
    disableSortBy: true
  }, {
    Header: '',
    id: 'loginAs',
    accessor: 'email',
    width: 76,
    Cell: _ref9 => {
      let {
        row
      } = _ref9;
      const status = row.original.status;
      const meRole = role;
      return /*#__PURE__*/_jsx(RequireAuth, {
        role: [UserRole.Admin, UserRole.Support],
        children: status === UserStatus.Active && (meRole === UserRole.Admin || role !== UserRole.Admin && role !== UserRole.Support) && /*#__PURE__*/_jsx(TooltipOnHover, {
          content: "Login as",
          placement: "top",
          variant: "inverted",
          size: "s",
          children: /*#__PURE__*/_jsx(Button, {
            onClick: () => loginAsUser(row.original.email),
            shape: "circle",
            variant: "flat",
            children: /*#__PURE__*/_jsx(LoginAsIcon, {
              size: 'xs'
            })
          })
        })
      });
    }
  }];
}
function ProductStatus(_ref10) {
  let {
    product: {
      access,
      id
    }
  } = _ref10;
  if (access.status === 'DENIED') return null;
  const AccessIcon = accessIconMap[access.accessType];
  const accessStatusDot = {
    ['GRANTED']: /*#__PURE__*/_jsx("div", {
      className: "UserItem__productTooltip__accessStatusDot UserItem__productTooltip__accessStatusDot--active"
    }),
    ['PENDING']: /*#__PURE__*/_jsx("div", {
      className: "UserItem__productTooltip__accessStatusDot UserItem__productTooltip__accessStatusDot--pending"
    }),
    ['EXPIRED']: /*#__PURE__*/_jsx("div", {
      className: "UserItem__productTooltip__accessStatusDot UserItem__productTooltip__accessStatusDot--expired"
    })
  }[access.status];
  return /*#__PURE__*/_jsx(TooltipOnHover, {
    content: /*#__PURE__*/_jsx(_Fragment, {
      children: /*#__PURE__*/_jsxs("div", {
        className: "UserItem__productTooltip",
        children: [/*#__PURE__*/_jsxs("div", {
          className: "UserItem__productTooltip-row",
          children: [/*#__PURE__*/_jsx("div", {
            className: "UserItem__productTooltip-lcolumn",
            children: /*#__PURE__*/_jsx(AccessIcon, {
              size: 'xs'
            })
          }), /*#__PURE__*/_jsxs("div", {
            className: "UserItem__productTooltip-rcolumn",
            children: [/*#__PURE__*/_jsx("div", {
              className: "UserItem__productTooltip__accessType",
              children: access.accessType
            }), /*#__PURE__*/_jsxs("div", {
              className: "UserItem__productTooltip__validDate",
              children: ["Valid to: ", access.expiryDate]
            })]
          })]
        }), /*#__PURE__*/_jsxs("div", {
          className: "UserItem__productTooltip-row ",
          children: [/*#__PURE__*/_jsx("div", {
            className: "UserItem__productTooltip-lcolumn",
            children: accessStatusDot
          }), /*#__PURE__*/_jsx("div", {
            className: "UserItem__productTooltip-rcolumn",
            children: /*#__PURE__*/_jsx("div", {
              className: "UserItem__productTooltip__accessStatus",
              children: constantToDisplayName(access.status)
            })
          })]
        })]
      })
    }),
    placement: "top",
    size: "s",
    "data-sentry-element": "TooltipOnHover",
    "data-sentry-component": "ProductStatus",
    "data-sentry-source-file": "createUsersListColumns.tsx",
    children: /*#__PURE__*/_jsxs("div", {
      className: cx('UserItem__product', access.status !== 'GRANTED' && 'UserItem__product--not-available'),
      children: [/*#__PURE__*/_jsx(ProductLogo, {
        productId: id,
        "data-sentry-element": "ProductLogo",
        "data-sentry-source-file": "createUsersListColumns.tsx",
        "data-sentry-element": "ProductLogo"
      }), access.accessType === AccessType.Evaluation && /*#__PURE__*/_jsx("span", {
        className: "UserItem__product-evaluation-icon",
        children: /*#__PURE__*/_jsx(TimeIcon, {})
      })]
    })
  });
}